import { useMemo, useState } from "react";
import { useSessionOverview } from "../api/hooks";

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const remS = s % 60;
  if (m < 60) return `${m}m ${remS}s`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return `${h}h ${remM}m`;
}

function formatAge(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function RateLimitBar({ label, pct }: { label: string; pct: number }) {
  const color =
    pct >= 80 ? "bg-red-500" : pct >= 50 ? "bg-amber-500" : "bg-green-500";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-zinc-500">{label}</span>
        <span className="text-zinc-400">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function AccountCard({ account, latestSession, expanded, onToggle, sessionCount }: {
  account: any;
  latestSession?: any;
  expanded: boolean;
  onToggle: () => void;
  sessionCount: number;
}) {
  const lastUsedBy = latestSession?.linuxName || latestSession?.linuxUsername;
  return (
    <button
      onClick={onToggle}
      className="w-full text-left bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-colors"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 mb-2">
            <svg
              className={`w-3.5 h-3.5 text-zinc-500 transition-transform shrink-0 ${expanded ? "rotate-90" : ""}`}
              fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            <span className="text-xs text-zinc-500">Account</span>
            <span className="text-xs font-semibold text-zinc-100">{account.email}</span>
          </div>
          <div className="ml-5.5 text-xs space-y-1.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
              <div>
                <span className="text-zinc-500">Org</span>
                <p className="font-semibold text-zinc-100">{account.orgName || "-"}</p>
              </div>
              <div>
                <span className="text-zinc-500">Plan</span>
                <p className="font-semibold text-zinc-100">{account.subscriptionType || "-"}</p>
              </div>
              <div>
                <span className="text-zinc-500">Last used by</span>
                <p className="font-semibold text-zinc-100">
                  {lastUsedBy ? (
                    <>
                      {lastUsedBy}
                      {latestSession.linuxEmail && <span> ({latestSession.linuxEmail})</span>}
                    </>
                  ) : "unknown"}
                  {" · "}{formatAge(account.lastReportedAt)}
                </p>
              </div>
              <div>
                <span className="text-zinc-500">Sessions</span>
                <p className="font-semibold text-zinc-100">{sessionCount}</p>
              </div>
            </div>
          </div>
        </div>
        {(account.rateLimitFiveHourPct != null || account.rateLimitSevenDayPct != null) && (
          <div className="grid grid-cols-1 gap-2 sm:w-44 shrink-0 ml-5.5 sm:ml-0">
            {account.rateLimitFiveHourPct != null && (
              <RateLimitBar label="5h limit" pct={account.rateLimitFiveHourPct} />
            )}
            {account.rateLimitSevenDayPct != null && (
              <RateLimitBar label="7d limit" pct={account.rateLimitSevenDayPct} />
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function SessionCard({ session }: { session: any }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <span className="text-zinc-500">Model</span>
          <p className="font-semibold text-zinc-100">{session.modelName}</p>
        </div>
        <div>
          <span className="text-zinc-500">CLI version</span>
          <p className="text-zinc-300 font-mono mt-0.5">{session.version}</p>
        </div>
        <div>
          <span className="text-zinc-500">User</span>
          <p className="text-zinc-300 mt-0.5">
            {session.linuxName || session.linuxUsername || "-"}
            {session.linuxEmail && <span className="text-zinc-500"> ({session.linuxEmail})</span>}
          </p>
        </div>
        <div>
          <span className="text-zinc-500">Last seen</span>
          <p className="text-zinc-300 mt-0.5">{formatAge(session.lastReportedAt)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <span className="text-zinc-500">Input tokens</span>
          <p className="text-zinc-300 font-mono mt-0.5">
            {session.totalInputTokens.toLocaleString()}
          </p>
        </div>
        <div>
          <span className="text-zinc-500">Output tokens</span>
          <p className="text-zinc-300 font-mono mt-0.5">
            {session.totalOutputTokens.toLocaleString()}
          </p>
        </div>
        <div>
          <span className="text-zinc-500">Cost</span>
          <p className="text-zinc-300 font-mono mt-0.5">
            ${(session.totalCostUsd ?? 0).toFixed(2)}
          </p>
        </div>
        <div>
          <span className="text-zinc-500">Duration</span>
          <p className="text-zinc-300 font-mono mt-0.5">
            {session.totalDurationMs ? formatDuration(session.totalDurationMs) : "-"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <span className="text-zinc-500">Lines added</span>
          <p className="text-green-400 font-mono mt-0.5">+{session.linesAdded ?? 0}</p>
        </div>
        <div>
          <span className="text-zinc-500">Lines removed</span>
          <p className="text-red-400 font-mono mt-0.5">-{session.linesRemoved ?? 0}</p>
        </div>
        <div>
          <span className="text-zinc-500">API time</span>
          <p className="text-zinc-300 font-mono mt-0.5">
            {session.totalApiDurationMs ? formatDuration(session.totalApiDurationMs) : "-"}
          </p>
        </div>
        <div>
          <span className="text-zinc-500">Context used</span>
          <p className="text-zinc-300 font-mono mt-0.5">
            {Number(session.contextUsedPct).toFixed(1)}% of {session.contextWindowSize >= 1_000_000 ? `${(session.contextWindowSize / 1_000_000).toFixed(0)}M` : `${(session.contextWindowSize / 1000).toFixed(0)}k`}
          </p>
        </div>
      </div>

      <div className="pt-1 border-t border-zinc-800/50 space-y-0.5 text-[11px]">
        <p className="truncate" title={session.cwd}>
          <span className="text-zinc-600">cwd</span>{" "}
          <span className="text-zinc-400 font-mono">{session.cwd}</span>
        </p>
        {session.hostname && (
          <p className="truncate">
            <span className="text-zinc-600">host</span>{" "}
            <span className="text-zinc-400 font-mono">{session.hostname}</span>
          </p>
        )}
        <p className="truncate" title={session.sessionId}>
          <span className="text-zinc-600">session</span>{" "}
          <span className="text-zinc-500 font-mono">{session.sessionId}</span>
        </p>
      </div>
    </div>
  );
}

export function ClaudeSessionsPage() {
  const overview = useSessionOverview();

  // Derive accounts and sessions from QueryState wrapper
  const accounts = overview.status === "ready" ? overview.data.accounts : undefined;
  const sessions = overview.status === "ready" ? overview.data.sessions : undefined;

  const { sessionsByEmail, orphanSessions } = useMemo(() => {
    if (!sessions) return { sessionsByEmail: new Map<string, any[]>(), orphanSessions: [] as any[] };
    const map = new Map<string, any[]>();
    const orphans: any[] = [];
    for (const s of sessions) {
      if (s.email) {
        const list = map.get(s.email) ?? [];
        list.push(s);
        map.set(s.email, list);
      } else {
        orphans.push(s);
      }
    }
    return { sessionsByEmail: map, orphanSessions: orphans };
  }, [sessions]);

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggleExpanded = (email: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const loading = overview.status === "loading";
  const empty = !loading && (accounts?.length === 0) && (sessions?.length === 0);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-zinc-100">Claude Sessions</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Account usage and active Claude Code sessions.
        </p>
      </div>

      {overview.status === "error" ? (
        <div className="text-sm text-red-400">Error: {overview.error}</div>
      ) : loading ? (
        <div className="text-sm text-zinc-600">Loading...</div>
      ) : empty ? (
        <div className="text-center py-16 text-zinc-600">
          <p className="text-sm">No sessions reported yet.</p>
          <p className="text-sm text-zinc-500 mt-1">
            Configure your statusline script to report session data.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {accounts?.map((account) => {
            const accountSessions = sessionsByEmail.get(account.email) ?? [];
            const latestSession = accountSessions[0];
            const isExpanded = expanded.has(account.email);
            return (
              <div key={account.email} className="space-y-3">
                <AccountCard
                  account={account}
                  latestSession={latestSession}
                  expanded={isExpanded}
                  onToggle={() => toggleExpanded(account.email)}
                  sessionCount={accountSessions.length}
                />
                {isExpanded && accountSessions.length > 0 && (
                  <div className="ml-4 border-l-2 border-zinc-800 pl-4 space-y-3">
                    {accountSessions.map((s) => (
                      <SessionCard key={s.sessionId} session={s} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {orphanSessions.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-zinc-600 uppercase tracking-wider">
                Unknown Account
              </h3>
              {orphanSessions.map((s) => (
                <SessionCard key={s.sessionId} session={s} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
